import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

// Define the structure of a search result
interface SearchResult {
  title: string;
  description: string;
  path: string;
  category: string;
}

// Knowledge base content index
const searchIndex: SearchResult[] = [
  // Getting Started
  {
    title: "Understanding Karta Pobytu Requirements",
    description: "Essential requirements and eligibility criteria",
    path: "/knowledge-base/karta-pobytu-requirements",
    category: "Getting Started"
  },
  {
    title: "Document Checklist for First-Time Applicants",
    description: "Complete list of required documents",
    path: "/knowledge-base/document-checklist",
    category: "Getting Started"
  },
  {
    title: "Step-by-Step Application Process",
    description: "Detailed guide to the application process",
    path: "/knowledge-base/application-process",
    category: "Getting Started"
  },
  {
    title: "Common Mistakes to Avoid",
    description: "Learn from others' experiences and avoid common pitfalls",
    path: "/knowledge-base/common-mistakes",
    category: "Getting Started"
  },
  // Documentation
  {
    title: "Passport Requirements",
    description: "Detailed passport requirements and specifications",
    path: "/knowledge-base/documentation/passport-requirements",
    category: "Documentation"
  },
  {
    title: "Income Proof Requirements",
    description: "Guidelines for proving sufficient financial means",
    path: "/knowledge-base/documentation/income-proof",
    category: "Documentation"
  },
  {
    title: "Housing Documentation Guide",
    description: "Requirements for proving accommodation",
    path: "/knowledge-base/documentation/housing-guide",
    category: "Documentation"
  },
  {
    title: "Health Insurance Requirements",
    description: "Health insurance documentation guidelines",
    path: "/knowledge-base/documentation/health-insurance",
    category: "Documentation"
  },
  // Processing Times
  {
    title: "Processing Times by Voivodeship",
    description: "Compare processing times across different Polish voivodeships",
    path: "/knowledge-base/processing-times/voivodeship-times",
    category: "Processing Times"
  },
  {
    title: "Track Application Status",
    description: "Step-by-step guide to checking your application progress",
    path: "/knowledge-base/processing-times/track-status",
    category: "Processing Times"
  },
  {
    title: "Expedited Processing Options",
    description: "Learn about available options for faster processing",
    path: "/knowledge-base/processing-times/expedited-options",
    category: "Processing Times"
  },
  {
    title: "Handling Processing Delays",
    description: "Guidelines and solutions for handling delays",
    path: "/knowledge-base/processing-times/handling-delays",
    category: "Processing Times"
  }
];

interface SearchBarProps {
  className?: string;
  placeholder?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({ 
  className,
  placeholder = "Search knowledge base..."
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (query.length >= 2) {
      const searchResults = searchIndex.filter(item => {
        const searchString = `${item.title} ${item.description} ${item.category}`.toLowerCase();
        return searchString.includes(query.toLowerCase());
      });
      setResults(searchResults);
      setIsSearching(true);
    } else {
      setResults([]);
      setIsSearching(false);
    }
  }, [query]);

  const handleResultClick = (path: string) => {
    setQuery('');
    setResults([]);
    setIsSearching(false);
    navigate(path);
  };

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="pl-9 pr-4 w-full"
        />
      </div>

      {/* Search Results Dropdown */}
      {isSearching && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-lg border border-border max-h-[400px] overflow-y-auto">
          {results.length > 0 ? (
            <div className="p-2">
              {results.map((result, index) => (
                <div
                  key={index}
                  className="p-3 hover:bg-accent rounded-md cursor-pointer transition-colors"
                  onClick={() => handleResultClick(result.path)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-foreground">{result.title}</h4>
                      <p className="text-xs text-muted-foreground mt-1">{result.description}</p>
                    </div>
                    <span className="text-xs text-muted-foreground bg-accent/50 px-2 py-1 rounded">
                      {result.category}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 